// Store Locator bootstrap
window.addEventListener("load", function () {
  // ---- Config / Debug -------------------------------------------------------
  const DEBUG = true;
  const NOMINATIM_EMAIL = "sc@nomilk.dk"; // set to a real contact email
  const NOMINATIM_ORIGIN = "dk";                 // country code filter
  const GEOCODE_BASE_DELAY_MS = 1000;            // base delay between geocodes
  const GEOCODE_MAX_ATTEMPTS = 3;                // retry cap per address

  // ---- Logger ---------------------------------------------------------------
  const TAG = "StoreLocator";
  const log = (...a) => DEBUG && console.log(`[${TAG}]`, ...a);
  const warn = (...a) => DEBUG && console.warn(`[${TAG}]`, ...a);
  const err = (...a) => DEBUG && console.error(`[${TAG}]`, ...a);
  const group = (label) => DEBUG && console.group(`[${TAG}] ${label}`);
  const groupEnd = () => DEBUG && console.groupEnd();
  const time = (label) => DEBUG && console.time(`[${TAG}] ${label}`);
  const timeEnd = (label) => DEBUG && console.timeEnd(`[${TAG}] ${label}`);

  // ---- State ----------------------------------------------------------------
  const mergedBlocks = Array.isArray(window.allMapBlocks) && typeof window.allMapBlocks.flat === "function"
    ? window.allMapBlocks.flat()
    : [];

  /** @type {{title:string, lat:number, lng:number, description:string}[]} */
  const allLocations = [];
  /** @type {{locationData:any, address:string, attempts:number}[]} */

  const geocodeQueue = [];
  let userCoordsCache = null;      // { latitude, longitude } or null
  let map = null;                  // Leaflet instance
  let markersGroup = null;         // L.FeatureGroup
  let distancesComputed = false;   // prevent duplicate "km" appends
  let pendingLocs = [];
  let commitTimer = null;
  let didInitialFit = false;

  // Add many markers at once, no repeated fitBounds
  function queueMarkers(locs, { fit = false } = {}) {
    if (!locs?.length) return;
    pendingLocs.push(...locs);
    // commit shortly (group DOM work)
    if (!commitTimer) commitTimer = setTimeout(() => commitMarkers({ fit }), 120);
  }

  function commitMarkers({ fit = false } = {}) {
    if (!map || !markersGroup || !pendingLocs.length) {
      commitTimer = null;
      return;
    }

    // Bulk add
    group("Commit markers");
    time("batch-add");

    pendingLocs.forEach((loc) => {
      if (!isValidLatLng(loc.lat, loc.lng)) return;
      const marker = L.marker([loc.lat, loc.lng], map.__markerIcon ? { icon: map.__markerIcon } : undefined)
        .bindPopup(`<h3>${loc.title}</h3><p>${loc.description}</p>`);
      markersGroup.addLayer(marker);
    });

    pendingLocs = [];
    timeEnd("batch-add");
    groupEnd();

    // One fit per batch (or just once on first load)
    if (fit) {
      const bounds = markersGroup.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
        didInitialFit = true;
      }
    }

    commitTimer = null;
  }
  // ---- Utilities ------------------------------------------------------------
  const parseNum = (v) => {
    if (typeof v === "string") v = v.replace(",", ".").trim();
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };

  const isValidLatLng = (lat, lng) =>
    Number.isFinite(lat) && Math.abs(lat) <= 90 &&
    Number.isFinite(lng) && Math.abs(lng) <= 180;

  function calculateDistance(coords1, coords2, unitSystem = "metric") {
    const dtor = Math.PI / 180;
    const radius = unitSystem === "metric" ? 6378.14 : 3959;
    const rlat1 = coords1.latitude * dtor;
    const rlong1 = coords1.longitude * dtor;
    const rlat2 = coords2.latitude * dtor;
    const rlong2 = coords2.longitude * dtor;
    const dlon = rlong2 - rlong1;
    const dlat = rlat2 - rlat1;
    const a = Math.sin(dlat / 2) ** 2 +
              Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(dlon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return radius * c;
  }

  function getUserLocation() {
    const storageKey = "cc-location";
    if (userCoordsCache) return Promise.resolve(userCoordsCache);

    return new Promise((resolve) => {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          const coords = JSON.parse(stored);
          if (coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)) {
            userCoordsCache = coords;
            log("Using cached session coords:", userCoordsCache);
            return resolve(coords);
          }
        }
      } catch (e) {
        warn("Failed to read cached coords:", e);
      }

      if (!navigator.geolocation) return resolve(null);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          };
          userCoordsCache = coords;

          // Optional Shopify localization update
          fetch("/localization.json", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(coords),
          }).catch(() => { /* ignore */ });

          try {
            sessionStorage.setItem(storageKey, JSON.stringify(coords));
          } catch (e) {
            warn("Failed to cache coords:", e);
          }

          resolve(coords);
        },
        () => resolve(null),
        { maximumAge: 3600000, timeout: 5000 }
      );
    });
  }

  // ---- Preprocess input blocks ---------------------------------------------
  group("Preprocess blocks");
  time("flatten+normalize");

  mergedBlocks.forEach((block, i) => {
    const s = block?.settings || {};
    const lat = parseNum(s.store_latitude);
    const lng = parseNum(s.store_longitude);

    const address = [
      (s.store_address || "").trim(),
      (s.store_city || "").trim(),
    ].filter(Boolean).join(", ");

    const locationData = {
      title: s.store_name || s.title || "",
      lat,
      lng,
      description: address || `${(s.store_zip_code || "").trim()} ${(s.store_country || "").trim()}`.trim(),
    };

    if (isValidLatLng(lat, lng)) {
      allLocations.push(locationData);
    } else if (address) {
      geocodeQueue.push({ locationData, address, attempts: 0 });
    } else {
      warn(`Block #${i} missing lat/lng and address, skipping`, block);
    }
  });

  timeEnd("flatten+normalize");
  log("Locations with coords:", allLocations.length);
  log("Locations to geocode:", geocodeQueue.length);
  groupEnd();

  // ---- Initialize Leaflet ---------------------------------------------------
  function initMap() {
    if (!document.getElementById("map")) {
      warn("#map not found, aborting map init");
      return;
    }

    const defaultCoords = [55.6761, 12.5683]; // Copenhagen fallback
    const initialZoom = 7;

    //map = L.map("map").setView(defaultCoords, initialZoom);
    map = L.map("map", { preferCanvas: true }).setView(defaultCoords, initialZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    // Expose for external functions that expect a global
    window.__storeLocatorMap = map;

    // Marker icon
    if (typeof window.mapIconUrl === "string" && window.mapIconUrl.length) {
      const mapIcon = L.icon({
        iconUrl: window.mapIconUrl,
        iconSize: [38, 40],
        iconAnchor: [22, 30],
        popupAnchor: [-3, -40],
      });
      map.__markerIcon = mapIcon;
    }

    markersGroup = L.featureGroup().addTo(map);
  }

  // ---- Render markers & distances ------------------------------------------
  function addMarkers(locations, { fit = false } = {}) {
    if (!map || !markersGroup) return;
    if (!locations?.length) return;

    group("Add markers");
    time("marker-add");

    locations.forEach((loc) => {
      if (!isValidLatLng(loc.lat, loc.lng)) return;

      const marker = L.marker([loc.lat, loc.lng], map.__markerIcon ? { icon: map.__markerIcon } : undefined)
        .bindPopup(`<h3>${loc.title}</h3><p>${loc.description}</p>`);
      markersGroup.addLayer(marker);
    });

    timeEnd("marker-add");
    groupEnd();

    // Fit bounds to all markers if any exist
    // const bounds = markersGroup.getBounds();
    // if (bounds.isValid()) {
    //   map.fitBounds(bounds, { padding: [30, 30] });
    // }
    if (fit) {
      const bounds = markersGroup.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }
  }

  function updateDistancesOnce() {
    if (distancesComputed) return;
    const items = document.querySelectorAll(".store-locator-maplist-item");
    if (!items.length) return;

    getUserLocation().then((user) => {
      if (!user) return;

      group("Compute distances");
      time("distances");

      items.forEach((el) => {
        const lat = parseNum(el.getAttribute("data-latitude"));
        const lng = parseNum(el.getAttribute("data-longitude"));
        if (!isValidLatLng(lat, lng)) return;

        const d = calculateDistance(user, { latitude: lat, longitude: lng }, "metric");
        const km = d.toFixed(1);

        const p = el.querySelector("p");
        const span = p ? p.querySelector("span") : null;

        if (span) {
          span.textContent = km;
        }
        if (p) {
          const text = p.textContent || "";
          if (!/(\s|^)km(\s|$)/i.test(text)) {
            p.append(" km");
          }
        }
        el.setAttribute("data-distance", String(km));
      });

      timeEnd("distances");
      groupEnd();

      distancesComputed = true;
    });
  }

  // ---- Geocoding queue (Nominatim) -----------------------------------------
  function geocodeNext(delayMs = GEOCODE_BASE_DELAY_MS) {
    if (!geocodeQueue.length) return;

    const item = geocodeQueue.shift();
    const url = `https://nominatim.openstreetmap.org/search?format=json&email=${encodeURIComponent(NOMINATIM_EMAIL)}&countrycodes=${encodeURIComponent(NOMINATIM_ORIGIN)}&q=${encodeURIComponent(item.address)}`;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          item.locationData.lat = Number(data[0].lat);
          item.locationData.lng = Number(data[0].lon);
          if (isValidLatLng(item.locationData.lat, item.locationData.lng)) {
            allLocations.push(item.locationData);
            //addMarkers([item.locationData]);
            queueMarkers([item.locationData], { fit: !didInitialFit });
          } else {
            warn("Geocode returned invalid lat/lng for:", item.address, data[0]);
          }
        } else {
          warn("No geocode result for:", item.address);
        }
      })
      .catch((e) => {
        item.attempts += 1;
        if (item.attempts < GEOCODE_MAX_ATTEMPTS) {
          // exponential backoff
          geocodeQueue.unshift(item);
          return setTimeout(() => geocodeNext(delayMs * 2), delayMs * 2);
        }
        err("Geocode failed, giving up:", item.address, e);
      })
      .finally(() => {
        if (geocodeQueue.length) {
          setTimeout(() => geocodeNext(GEOCODE_BASE_DELAY_MS), GEOCODE_BASE_DELAY_MS);
        } else {
          // After last geocode: compute distances if not already done
          updateDistancesOnce();
        }
      });
  }

  // ---- List count & sorting -------------------------------------------------
  function updateBannerCount() {
    const items = document.querySelectorAll(".store-locator-maplist-item");
    const el = document.querySelector(".store-locator-maplist-count");
    if (el) el.textContent = String(items.length);
  }

  function sortStoresListByLocation(mapInstance) {
    log("[INFO] sortStoresListByLocation");
    const listRoot = document.querySelector(".store-locator-maplist");
    if (!listRoot) return;

    const nodes = Array.from(document.querySelectorAll(".store-locator-maplist-item"));
    if (!nodes.length) return;

    nodes.sort((a, b) => {
      const aDist = parseNum(a.querySelector("p span")?.textContent) || Number.POSITIVE_INFINITY;
      const bDist = parseNum(b.querySelector("p span")?.textContent) || Number.POSITIVE_INFINITY;
      return aDist - bDist;
    });

    const frag = document.createDocumentFragment();
    nodes.forEach((n) => frag.appendChild(n));
    listRoot.innerHTML = "";
    listRoot.appendChild(frag);

    const firstLat = parseNum(nodes[0].getAttribute("data-latitude"));
    const firstLng = parseNum(nodes[0].getAttribute("data-longitude"));
    if (mapInstance && isValidLatLng(firstLat, firstLng)) {
      mapInstance.setView([firstLat, firstLng], mapInstance.getZoom());
    }
  }

  // ---- Map/List plumbing from your second load block ------------------------
  function reparentMapAndLists() {
    const allRetailerSections = document.querySelectorAll(".leaflet-maps-section");
    const theMap = document.getElementById("map");
    const listContainer = document.querySelector(".map-list-container");
    const mapContainer = document.querySelector(".map-container");

    console.log(`Found ${mapContainer}`);
    if (allRetailerSections.length && listContainer) {
      allRetailerSections.forEach((section) => listContainer.appendChild(section));
    }

    if (theMap && mapContainer) {
      mapContainer.appendChild(theMap);
    }
  }

  // ---- Public helpers you had ------------------------------------------------
  window.toggle_map = function toggle_map(element) {
    const remove_maplist = document.querySelectorAll(".remove-maplist-item");
    if (element.className.includes("toggle-icon-map")) {
      $(".toggle-icon-list").addClass("show");
      $(".store-locator-maplist-wrap").hide();
      $(".store-locator-map").removeClass("hidden-on-mobile");
    } else if (element.className.includes("toggle-icon-list")) {
      $(".toggle-icon-map").addClass("show");
      $(".store-locator-maplist-wrap").show();
      $(".store-locator-map").addClass("hidden-on-mobile");
      remove_maplist.forEach((item) => (item.style.display = "none"));
    }
  };

  window.panToLocation = function panToLocation(block_id) {
    const lat = parseNum($("#info" + block_id + " .store-item__distance").data("latitude"));
    const lng = parseNum($("#info" + block_id + " .store-item__distance").data("longitude"));
    if (isValidLatLng(lat, lng) && window.__storeLocatorMap) {
      if ($(window).width() <= 768) {
        $(".store-locator-maplist-wrap").hide();
        $(".store-locator-map").removeClass("hidden-on-mobile");
        $(".toggle-icon-map").removeClass("show");
        $(".toggle-icon-list").addClass("show");
      }
      window.__storeLocatorMap.setView([lat, lng], 15);
      $(".store-locator-maplist .store-locator-maplist-item").removeClass("lid-selected");
      $("#lid" + block_id).addClass("lid-selected");
      $(".store-locator-map .store-locator-maplist-item").remove();
    }
  };

  // ---- Kickoff --------------------------------------------------------------
  group("Init");
  time("total-init");

  reparentMapAndLists();
  initMap();

  if (map) {
    updateBannerCount();
    //addMarkers(allLocations);     // render what we already have
    queueMarkers(allLocations, { fit: true });
    updateDistancesOnce();        // compute once when possible
    if (geocodeQueue.length) {
      geocodeNext();              // resolve missing coords progressively
    }

    // If you need list sorting after distances:
    getUserLocation().then(() => {
      // ensure distances computed, then sort
      setTimeout(() => sortStoresListByLocation(map), 50);
    });
  }
 
  timeEnd("total-init");
  groupEnd();

  // ---- Optional: IP → Country debug helper (unchanged but hardened) --------
//   window.userCountryLatLng = function userCountryLatLng(ip) {
//     const accessKey = "f1c664dbb5d87e06b3a26bffd257cc2d";
//     fetch(`https://api.ipapi.com/api/${ip}?access_key=${accessKey}`)
//       .then((r) => r.json())
//       .then((data) => {
//         log("User IP:", data.ip);
//         log("Country:", data.country_name);
//         log("Region:", data.region_name);
//         log("Latitude:", data.latitude);
//         log("Longitude:", data.longitude);
//       })
//       .catch((e) => err("ipapi error:", e));
//   };

//   window.getCapitalCity = function getCapitalCity(countryName) {
//     if (!countryName) return warn("getCapitalCity: countryName missing");
//     fetch(`https://restcountries.com/v3.1/name/${encodeURIComponent(countryName)}`)
//       .then((r) => r.json())
//       .then((data) => {
//         const capital = data?.[0]?.capital?.[0];
//         if (capital) {
//           log(`Capital of ${countryName}:`, capital);
//         } else {
//           warn("Capital not found for:", countryName);
//         }
//       })
//       .catch((e) => err("restcountries error:", e));
//   };

  // ---- One-time load debug you had -----------------------------------------
  (function onLoadFinalTouches() {
    log("----- window load -----");
    const ip = navigator.ip ? navigator.ip : "194.182.37.4";
    log("USED IP:", ip);
    //window.userCountryLatLng(ip);
  })();
});
